import React, { useState, useMemo, useEffect, use } from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  FormGroup,
  FormFeedback,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Alert,
} from 'reactstrap';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useFormik } from 'formik';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/TableContainer';

import moment from 'moment';
import { useParams, Link } from 'react-router-dom';
import logo from '../../assets/images/logo-dark2.png';
import { useMutation, useQueryClient } from 'react-query';
import { getTransactionApi } from 'api/transaction';
import {
  getClaimDetailsByIdApi,
  getClaimTimelineByIdApi,
  approveClaimApi,
  rejectClaimApi,
  disburseClaimApi,
  getClaimAttachmentsByIdApi,
  forwardClaimApi,
} from 'api/claim';
import { getAllSubRolesApi } from 'api/role';
import { useRole } from 'hooks/useRole';
import {
  MemberId,
  BillingName,
  // Date,
  Total,
  PaymentStatus,
  PaymentMethod,
} from '../Members/MemberCol';
import { SelectField } from 'components/Common/fields';
import notify from 'utils/notify';

// Principal death pays next of kin; dependent death pays the member.
const isPrincipalDeath = (data) =>
  data?.type === 'Principal' ||
  (data?.memberId != null && data?.memberId === data?.deathCertificate?.dependentId);

// Canonicalise a Kenyan mobile to 254XXXXXXXXX (mirrors backend Utils.getValidMobile).
// Never use a number input for phone numbers — it silently drops leading zeros.
const toValidMobile = (input) => {
  if (input == null || input === '') return input;
  const digits = String(input).replace(/\D/g, '');
  if (digits.length < 9) return input;
  return `254${digits.slice(-9)}`;
};

const getPayoutNumber = (data) => {
  if (!data) return '';
  const raw = isPrincipalDeath(data)
    ? data?.nextOfKin?.mobileNumber || data?.mobileNumber || ''
    : data?.member?.mobileNumber1 || data?.mobileNumber || '';
  return toValidMobile(raw);
};

const ClaimDetail = () => {
  const dateFormat = 'DD-MM-YYYY';
  const { isFacilitator, isFinance, isAdmin, isDataValidator, isSuperAdmin } =
    useRole(); // Add other roles as needed
  const { id } = useParams();
  const [latestTransaction, setLatestTransaction] = useState([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const [forward, setForward] = useState(false);
  const [pages, setPages] = useState(0);
  const roleId = localStorage.getItem('shofco_role_id');
  const queryClient = useQueryClient();
  const [claimData, setClaimData] = useState(null); // State to store claim details
  const [attachmentData, setAttachmentData] = useState([]);
  const [comments, setComments] = useState('');
  const [amount, setAmount] = useState('');
  const [recipientNumber, setRecipientNumber] = useState('');
  const [modal, setModal] = useState(null); // Use null instead of boolean to track modal type
  const toggleModal = () => setModal(null);
  const [timelineData, setTimelineData] = useState([]);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
  const selectLoginState = (state) => state.Login;
  const LoginProperties = createSelector(selectLoginState, (login) => {
    console.log('Login state in ClaimDetails:', login);
    return {
      error: login.error,
      user: login.user,
      loading: login.loading,
    };
  });

  const { error, user, loading } = useSelector(LoginProperties);
  // console.log('User in ClaimDetails:', user, 'Loading:', loading, 'Error:', error);

  // console.log('Role Id:', roleId, 'isFacilitator:', isFacilitator, 'isFinance:', isFinance, 'isAdmin:', isAdmin, 'isDataValidator:', isDataValidator, 'isSuperAdmin:', isSuperAdmin);

  const [pagination, setPagination] = useState({
    pageSize: 7,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    searchColumns: [
      { name: 'memberId', value: null },
      { name: 'endDate', value: moment().format(dateFormat) },
      {
        name: 'startDate',
        value: moment().subtract(1, 'year').format(dateFormat),
      },
    ],
  });

  // Mutation to fetch claim details
  const { mutate: mutateClaim, isLoading: isLoadingApplication } = useMutation(
    (payload) => getClaimDetailsByIdApi(payload),
    {
      onSuccess: (data) => {
        console.log('Fetched Claim Details:', data);
        setClaimData(data);
        if (data?.eligibilityProblems?.length) {
          notify.warning('This claim fails the cover rules', data.eligibilityProblems.join(' '));
        }
        setAmount(data?.claimAmount);
        setRecipientNumber(getPayoutNumber(data));
        // Fetch latest transactions for the member
        setPagination((prev) => ({
          ...prev,
          searchColumns: prev.searchColumns.map((col) =>
            col.name === 'memberId'
              ? { ...col, value: String(data.memberId) }
              : col,
          ),
        }));
      },
      onError: (error) => {
        console.error('Error fetching claim details:', error);
        notify.error('Error', 'Failed to fetch claim details.');
      },
    },
  );

  // Mutation to approve claim
  const { mutate: mutateApproveClaim, isLoading: isLoadingApprove } =
    useMutation((payload) => approveClaimApi(payload), {
      onSuccess: () => {
        notify.success('Claim Approved', 'The claim has been successfully approved!');
        queryClient.invalidateQueries('get-all-counties');
      },
      onError: (err) => {
        notify.error('Approval Failed', err?.message || 'Something went wrong while approving the claim.');
      },
    });

  // Mutation to reject claim
  const { mutate: mutateRejectClaim, isLoading: isLoadingReject } = useMutation(
    (payload) => rejectClaimApi(payload),
    {
      onSuccess: () => {
        notify.success('Claim Rejected', 'The claim has been rejected!');
        queryClient.invalidateQueries('get-all-counties');
      },
      onError: () => {
        notify.error('Rejection Failed', 'Something went wrong while rejecting the claim.');
      },
    },
  );

  // Mutation to disburse claim
  const { mutate: mutateDisburseClaim, isLoading: isLoadingDisburse } =
    useMutation((payload) => disburseClaimApi(payload), {
      onSuccess: () => {
        notify.success('Claim Disbursed', 'The claim has been successfully disbursed!');
        queryClient.invalidateQueries('get-all-counties');
      },
      onError: () => {
        notify.error('Disbursement Failed', 'Something went wrong while disbursing the claim.');
      },
    });

  useEffect(() => {
    const fetchClaimData = async () => {
      try {
        mutateClaim({ claimId: id });

        const [attachmentsResponse, timelineResponse] = await Promise.all([
          getClaimAttachmentsByIdApi(id),
          getClaimTimelineByIdApi(id),
        ]);

        setAttachmentData(attachmentsResponse);
        setTimelineData(timelineResponse?.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchClaimData();
  }, [id, mutateClaim]);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await getTransactionApi({
          pageNumber: pagination.pageNumber - 1,
          pageSize: pagination.pageSize,
          sortBy: pagination.sortBy,
          ascendingSort: pagination.ascendingSort,
          searchColumns: pagination.searchColumns,
        });
        console.log('Fetched Transactions:', response);
        setLatestTransaction(response.list);
        setPages(response.pages);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }
    };
    if (
      pagination.searchColumns.find((c) => c.name === 'memberId' && c.value)
    ) {
      fetchTransactions();
    }
    if (isSuperAdmin) {
      mutateSubRoles({ roleId: 2 });
    }
  }, [pagination]);

  // Fetch claim details and attachments on mount
  useEffect(() => {
    const fetchClaimData = async () => {
      try {
        mutateClaim({ claimId: id });
        const attachmentsResponse = await getClaimAttachmentsByIdApi(id);
        setAttachmentData(attachmentsResponse);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchClaimData();
  }, [id, mutateClaim]);

  // Handle approve/reject submission
  const handleSubmit = () => {
    if (comments.trim() === '') {
      notify.warning('Comments Required', 'Please provide comments before submitting.');
      return;
    }

    if (modal === 'approve') {
      mutateApproveClaim({
        claimId: id,
        comments,
        amount: isFinance ? amount : undefined, // Only include amount for finance role
        clientMobile: isDataValidator ? toValidMobile(recipientNumber) : undefined, // Only include amount for verfier role
      });
    } else if (modal === 'reject') {
      mutateRejectClaim({
        claimId: id,
        comments,
      });
    }
    toggleModal();
  };

  // Initiate disbursement
  const initiateDisbursement = () => {
    if (!claimData) return;
    mutateDisburseClaim({
      originatorConversationId: `1737986148199-122802-${id}`,
      claimId: id,
      memberId: claimData.memberId,
      memberMobile: claimData.member.mobileNumber1,
      policyId: claimData.policyId,
      approvedAmount: amount,
      approvedPhoneNumber: toValidMobile(recipientNumber),
      claimAmount: claimData.claimAmount || 100, // Fallback to 100 if claimAmount is null
    });
  };

  // Meta title
  document.title = 'Claim Details | SUN Welfare Administration System';

  const onChangePagination = ({ page, limit }, filters, sorter) => {
    let searchColumns =
      !filters || Object.keys(filters).length === 0
        ? pagination.searchColumns
        : [
          ...pagination.searchColumns,
          ...(filters?.id ? [{ name: 'id', value: filters?.id }] : []),
          ...(filters?.firstName
            ? [{ name: 'firstName', value: filters?.firstName }]
            : []),
          ...(filters?.lastName
            ? [{ name: 'lastName', value: filters?.lastName }]
            : []),
          ...(filters?.email ? [{ name: 'email', value: filters?.email }] : []),
          ...(filters?.transactionNumber
            ? [{ name: 'transactionNumber', value: filters?.transactionNumber }]
            : []),
          ...(filters?.idNumber
            ? [{ name: 'idNumber', value: filters?.idNumber }]
            : []),
          ...(filters?.mobileNumber1
            ? [{ name: 'mobileNumber1', value: filters?.mobileNumber1 }]
            : []),
        ];
    setPagination({
      ...pagination,
      pageNumber: page || pagination.pageNumber,
      pageSize: limit || pagination.pageSize,
      searchColumns:
        filters?.transactionNumber || filters?.idNumber
          ? searchColumns.filter(
            (s) => s.name !== 'startDate' && s.name !== 'endDate',
          )
          : searchColumns,
    });
  };

  const Transactioncolumns = useMemo(
    () => [
      {
        Header: '#',
        filterable: false,
        disableFilters: true,
        Cell: (cellProps) => {
          return <input type="checkbox" className="form-check-input" />;
        },
      },
      {
        Header: 'ID',
        accessor: 'id',
        filterable: false,
        disableFilters: true,
        Cell: (cellProps) => <MemberId {...cellProps} />,
      },
      {
        Header: 'Transaction ID',
        accessor: 'transactionNumber',
        filterable: false,
        disableFilters: true,
        Cell: (cellProps) => <MemberId {...cellProps} />,
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Date',
        accessor: 'transactionDateTime',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => {
          const formattedDate = moment(cellProps.value).format('DD-MM-YYYY');
          return <span>{formattedDate}</span>;
        },
      },
      {
        Header: 'Account',
        accessor: 'account',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Payment Status',
        accessor: 'status',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <PaymentStatus {...cellProps} />,
      },
      {
        Header: 'Reallocated Payment',
        accessor: (row) => {
          return row?.reAllocated
            ? `Reallocated from ${row.reAllocatedFromAccount} → ${row.account} on ${moment(row.reAllocatedAt).utcOffset('+0300').format('DD/MM/YYYY [at] h:mma')}`
            : 'NO';
        },
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Payment Method',
        accessor: 'paymentMode',
        disableFilters: true,
        Cell: (cellProps) => <PaymentMethod {...cellProps} />,
      },
    ],
    [],
  );

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      destinationRoleId: '',
    },
    validationSchema: Yup.object({
      destinationRoleId: Yup.string().required('Please Select Role'),
    }),
    onSubmit: async (values) => {
      try {
        console.log('Forwarding to Role:', values);
        setIsForwarding(true);
        let data = {
          ...claimData.claimStatus,
          ...values,
          sourceRoleId: subRolesData.find(r => r.name === claimData.claimStatus.destinationRole)?.id,
        };
        await forwardClaimApi(data);
        notify.success('Claim Forwarded', 'The claim has been successfully forwarded!');
        toggleForward();
      } catch (error) {
        console.error('Error finding role ID:', error);

        notify.error('Forwarding Failed', error?.message || 'Something went wrong while forwarding the claim.');
      } finally {
        setIsForwarding(false);
      }
    },
  });

  const {
    mutate: mutateSubRoles,
    isLoading: isLoadingSubRoles,
    data: subRolesData,
  } = useMutation((payload) => getAllSubRolesApi(payload), {
    onSettled: () => {
      queryClient.invalidateQueries('/public/um/role/');
    },
  });

  const toggleForward = () => {
    setForward(!forward);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Claims" breadcrumbItem="Claim Details" />
        {claimData?.eligibilityProblems?.length > 0 && (
          <Alert color="danger">
            <h5 className="alert-heading">This claim fails the cover rules — reject it</h5>
            <ul className="mb-0">
              {claimData.eligibilityProblems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Alert>
        )}
        <Row>
          {(isLoadingApplication || loading) && <div>Loading...</div>}
          {!isLoadingApplication && claimData && (
            <Col lg="12">
              <Link to="/claims/all">
                <Button
                  type="button"
                  color="primary"
                  className="btn text-end mb-3"
                >
                  <i className="bx bx-arrow-back"></i> Back
                </Button>
              </Link>
              <Card style={{ maxWidth: '70%', margin: '0 auto' }}>
                <CardBody>
                  <Row className="mb-5">
                    <Col className="text-end">
                      {claimData?.status === 'Inactive' ? (
                        <span className="badge badge-soft-dark font-size-13 p-2">
                          <i className="bx bx-lock-alt me-1"></i>
                          This claim is on Inactive hold. Contact an Admin to release.
                        </span>
                      ) : (
                        <>
                          {isSuperAdmin && (
                            <Button
                              type="button"
                              color="primary"
                              onClick={toggleForward}
                              className="me-4"
                              disabled={isForwarding}
                            >
                              <i className="bx bx-check"></i> Forward
                            </Button>
                          )}
                          {Number(claimData?.claimStatus?.destinationRoleId) ===
                            Number(roleId) || (isFacilitator && claimData?.claimStatus?.status === 'Revise' && claimData?.claimStatus?.actionBy !== user?.id) ? (
                            <>
                              <Button
                                type="button"
                                color="primary"
                                onClick={() => setModal('approve')}
                                className="me-4"
                                disabled={isLoadingApprove || isLoadingReject}
                              >
                                <i className="bx bx-check"></i> Approve
                              </Button>
                              {!isFacilitator && (
                                <Button
                                  type="button"
                                  color="danger"
                                  onClick={() => setModal('reject')}
                                  disabled={isLoadingApprove || isLoadingReject}
                                >
                                  <i className="bx bx-x"></i> Reject
                                </Button>
                              )}
                            </>
                          ) : (
                            <span>Claim processed</span>
                          )}
                          {isAdmin &&
                            claimData?.claimStatus?.status === 'Approved' && (
                              <Button
                                type="button"
                                color="success"
                                onClick={initiateDisbursement}
                                className="ms-4"
                                disabled={isLoadingDisburse}
                              >
                                <i className="bx bx-money"></i> Disburse
                              </Button>
                            )}
                        </>
                      )}
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <div className="mb-4">
                        <img
                          src={logo}
                          alt="Company Logo"
                          style={{ maxWidth: '200px' }}
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <h5>Claim Status</h5>
                      <p>{claimData?.claimStatus?.status || 'N/A'}</p>
                    </Col>
                    <Col md={4}>
                      <h5>Date Reported</h5>
                      <p>
                        {claimData?.reportedDateTime
                          ? moment(claimData.reportedDateTime).format(
                            'MMM DD, YYYY HH:mm',
                          )
                          : 'N/A'}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Row>
                        <h4>Member Details</h4>
                        <Col md={6}>
                          <p>Full Names:</p>
                          <p>Mobile:</p>
                          <p>IdNumber:</p>
                          <p>Location:</p>
                        </Col>
                        <Col md={6} style={{ fontWeight: 'bold' }}>
                          <p>
                            {claimData?.member
                              ? `${claimData.member.firstName || ''} ${claimData.member.lastName || ''
                              }`
                              : 'N/A'}
                          </p>
                          <p>{claimData?.member?.mobileNumber1 || 'N/A'}</p>
                          <p>
                            {claimData?.member?.identificationNumber || 'N/A'}
                          </p>
                          <p>
                            {claimData?.member?.addresses?.length
                              ? `${claimData.member.addresses[0].wardName}, ${claimData.member.addresses[0].constituencyName}`
                              : 'N/A'}
                          </p>
                        </Col>
                      </Row>
                      <hr />
                    </Col>
                    <Col>
                      <hr />
                      <Row>
                        <h4>Next of Kin Details</h4>
                        <Col md={6}>
                          <p>Full Names:</p>
                          <p>Mobile:</p>
                          <p>Email:</p>
                          <p>Relationship:</p>
                        </Col>
                        <Col md={6} style={{ fontWeight: 'bold' }}>
                          <p>
                            {claimData?.nextOfKin
                              ? `${claimData.nextOfKin.firstName || ''} ${claimData.nextOfKin.lastName || ''
                              }`
                              : 'N/A'}
                          </p>
                          <p>{claimData?.nextOfKin?.mobileNumber || 'N/A'}</p>
                          <p>{claimData?.nextOfKin?.email || 'N/A'}</p>
                          <p>{claimData?.nextOfKin?.relationship || 'N/A'}</p>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Row>
                        <h4>Deceased Details</h4>
                        <Col md={6}>
                          <p>Full Names:</p>
                          <p>Date of Death:</p>
                          <p>BurialPermitSerialNumber:</p>
                          <p>Age:</p>
                        </Col>
                        <Col md={6} style={{ fontWeight: 'bold' }}>
                          <p>
                            {claimData?.deathCertificate
                              ? `${claimData.deathCertificate.firstName || ''
                              } ${claimData.deathCertificate.lastName || ''}`
                              : 'N/A'}
                          </p>
                          <p>
                            {claimData?.deathCertificate?.deathDate
                              ? moment(
                                claimData?.deathCertificate.deathDate,
                              ).format('MMM DD, YYYY')
                              : 'N/A'}
                          </p>
                          <p>{claimData?.burialPermitSerialNumber || 'N/A'}</p>
                          <p>{claimData?.deathCertificate?.age || 'N/A'}</p>
                        </Col>
                      </Row>
                    </Col>
                    <Col md={6}>
                      <Row>
                        <h4>Payment Details</h4>
                        <Col md={6}>
                          <p>Mobile Number:</p>
                          <p>Amount:</p>
                        </Col>
                        <Col md={6} style={{ fontWeight: 'bold' }}>
                          <p>
                            {getPayoutNumber(claimData) || 'N/A'}
                            {isPrincipalDeath(claimData) && (
                              <small className="text-muted fw-normal d-block">
                                Paid to Next of Kin
                              </small>
                            )}
                          </p>
                          <p>{claimData?.claimAmount}</p>
                        </Col>
                      </Row>
                    </Col>
                    <hr />
                  </Row>
                  <Row>
                    <div className="mb-4 h4 card-title">Latest Transaction</div>
                    <TableContainer
                      columns={Transactioncolumns}
                      pages={pages}
                      data={
                        latestTransaction
                          ? [...latestTransaction].reverse()
                          : []
                      }
                      isGlobalFilter={false}
                      isAddOptions={false}
                      isPagination={true}
                      iscustomPageSizeOptions={false}
                      customPageSize={7}
                      // showView
                      // handleView={handleViewTransaction}
                      paginationData={pagination}
                      onChangePagination={onChangePagination}
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  </Row>
                  {
                    claimData.delayJustification && (
                      <Row className="mt-2">
                        <Col>
                          <h3>Delay Justification:</h3>
                          <p className="text-danger font-size-20">
                            {claimData?.delayJustifications}
                          </p>
                        </Col>
                      </Row>
                    )
                  }
                  <Row className="mt-2">
                    <Col>
                      <h4>Attachments:</h4>
                      <ol>
                        {attachmentData.length > 0 ? (
                          attachmentData.map((attachment) => (
                            <li className="mb-2" key={attachment.id}>
                              <div>
                                <span className="me-3">
                                  Name: <strong>{attachment.type}</strong>
                                </span>
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {attachment.name}
                                </a>
                              </div>
                            </li>
                          ))
                        ) : (
                          <li>No attachments available</li>
                        )}
                      </ol>
                    </Col>
                    <Col>
                      <h3>Comments:</h3>
                      <p className="text-danger font-size-20">
                        {claimData?.claimStatus?.comments}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      <h4 className="mb-3">Claim Timeline</h4>

                      {isLoadingTimeline ? (
                        <p>Loading timeline...</p>
                      ) : (
                        <div className="timeline-wrapper">
                          <ul className="list-unstyled">

                            {timelineData
                              ?.slice()
                              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                              .map((item, index) => (
                                <li key={index} className="mb-4 position-relative">

                                  {/* vertical line */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: '10px',
                                      top: 0,
                                      bottom: 0,
                                      width: '2px',
                                      background: '#e0e0e0',
                                    }}
                                  />

                                  {/* dot */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: '4px',
                                      top: '6px',
                                      width: '14px',
                                      height: '14px',
                                      borderRadius: '50%',
                                      background:
                                        item.type === 'CREATED'
                                          ? '#28a745'
                                          : item.type === 'REVISION'
                                            ? '#ffc107'
                                            : item.type === 'ATTACHMENT_UPLOADED'
                                              ? '#17a2b8'
                                              : '#6c757d',
                                    }}
                                  />

                                  {/* content */}
                                  <div style={{ marginLeft: '40px' }}>
                                    <div className="d-flex justify-content-between">
                                      <strong>{item.type}</strong>
                                      <small className="text-muted">
                                        {moment(item.timestamp).format('DD MMM YYYY, HH:mm')}
                                      </small>
                                    </div>

                                    <p className="mb-1">{item.description}</p>

                                    {item.actor && (
                                      <small className="text-muted">
                                        By: {item.actor.name} ({item.actor.email})
                                      </small>
                                    )}

                                    {item.metadata && item.metadata.status && (
                                      <div>
                                        <span className="badge bg-secondary">
                                          {item.metadata.status}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Modal isOpen={!!modal} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>
                      {modal === 'approve' ? 'Approve Claim' : 'Reject Claim'}
                    </ModalHeader>
                    <ModalBody>
                      <Label for="comments">Comments:</Label>
                      <Input
                        type="textarea"
                        id="comments"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Enter comments..."
                      />
                      {isDataValidator && modal === 'approve' && (
                        <>
                          <Label for="amount" className="mt-3">
                            Approved Phone Number:
                          </Label>
                          <Input
                            type="tel"
                            inputMode="numeric"
                            id="phoneNumber"
                            value={recipientNumber}
                            onChange={(e) => setRecipientNumber(e.target.value)}
                            onBlur={(e) =>
                              setRecipientNumber(toValidMobile(e.target.value))
                            }
                            placeholder="Enter Phone Number..."
                          />
                        </>
                      )}
                      {isFinance && modal === 'approve' && (
                        <>
                          <Label for="amount" className="mt-3">
                            Approved Amount:
                          </Label>
                          <Input
                            type="number"
                            id="amount"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="Enter amount..."
                          />
                          {/* <Label for="amount" className="mt-3">
                            Recipient Number:
                          </Label>
                          <Input
                            type="text"
                            id="recipientNumber"
                            value={recipientNumber}
                            onChange={(e) => setRecipientNumber(e.target.value)}
                            placeholder="Enter Recipient Number..."
                          /> */}
                        </>
                      )}
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isLoadingApprove || isLoadingReject}
                      >
                        Submit
                      </Button>
                      <Button color="secondary" onClick={toggleModal}>
                        Cancel
                      </Button>
                    </ModalFooter>
                  </Modal>
                  {isSuperAdmin && (
                    <Modal isOpen={!!forward} toggle={toggleForward}>
                      <ModalHeader toggle={toggleForward}>
                        Move Claim
                      </ModalHeader>
                      <ModalBody>
                        <FormGroup>
                          <Label htmlFor="destinationRoleId">Select Destination Role</Label>
                          <SelectField
                            name="destinationRoleId"
                            placeholder="Select Role"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.destinationRoleId || ''}
                            invalid={
                              validation.touched.destinationRoleId && validation.errors.destinationRoleId
                                ? true
                                : false
                            }
                          >
                            <option key={''}>Select Destination Role</option>
                            {subRolesData &&
                              subRolesData.map(({ id, name }) => (
                                <option value={id} key={id}>
                                  {name}
                                </option>
                              ))}
                          </SelectField>
                          {validation.touched.destinationRoleId && validation.errors.destinationRoleId ? (
                            <FormFeedback type="invalid">
                              {validation.errors.destinationRoleId}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="primary"
                          type='submit'
                          onClick={validation.handleSubmit}
                          disabled={isForwarding}
                        >
                          Submit
                        </Button>
                        <Button color="secondary" onClick={toggleForward}>
                          Cancel
                        </Button>
                      </ModalFooter>
                    </Modal>
                  )}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
};

export default ClaimDetail;
